/**
 * Returns true if the logged-in user has access to the named feature in
 * the current practice. Mirrors `useFeaturePermission` for non-component
 * code (Redux thunks, utility callers) — reads the same Redux slice via
 * `store.getState()`.
 *
 * Use this to early-return thunks that hit feature-gated BE endpoints so
 * we don't generate 403 noise in Sentry for practices that don't have
 * the feature enabled. The BE 403 is correct — the FE should just not
 * call in the first place.
 *
 * ## practiceId arg (recommended)
 *
 * Pass the practiceId you're about to fetch for as the second arg. When
 * provided, this helper compares it against the practiceId the current
 * permissions snapshot was fetched for (and the in-flight target while
 * `fetchMyPermissions` is loading) and refuses to green-light a stale
 * read. This closes the practice-switch window where the header has
 * dispatched `fetchMyPermissions(newPracticeId)` and `loading=true`, but
 * `state.permissions.permissions` still reflects the *old* practice
 * — any auto-mount useEffect that fires in that window would otherwise
 * pierce the bootstrap-safe gate and emit a 403 to Sentry (issues
 * DENTALSEM-CRM-1BS3, 1CHY, 1D6X-series, etc.).
 *
 * ## Legacy callers (when practiceId is omitted)
 *
 * Even without an explicit `expectedPracticeId`, this helper now refuses
 * the call when the snapshot in the slice belongs to a *different*
 * practice than the currently in-flight load. Rationale: dozens of
 * existing `hasFeatureAccess('feature')` call sites (mailUtils,
 * tiktokIntegration, whatsappV2, chat, campaign, dentallyApi, etc.)
 * predate the practice-id arg and cannot all be patched without churn;
 * by detecting cross-practice staleness inside the helper itself, they
 * all gain practice-switch protection for free. The only thing a legacy
 * caller misses is the post-settle stale-snapshot check (snapshot for
 * practice A still loaded after the user switched to practice B and the
 * fetch completed for B without the caller naming a practice). That
 * scenario is rare in practice because `fetchMyPermissions` is dispatched
 * synchronously at the start of the switch in the header.
 *
 * ## BOOTSTRAP-SAFE
 *
 * Returns `true` (i.e. "let the call go") on the very first bootstrap
 * window when `state.permissions.permissions` is still empty AND no
 * snapshot has ever been stored. There is a real window after login
 * where components mount and dispatch their initial fetches before
 * `fetchMyPermissions` resolves. If we returned `false` in that window
 * we'd silently swallow every legitimate first-load thunk and users
 * would see empty dashboards forever. The trade-off is at most one
 * round-trip of 403 noise on the very first page load per session vs.
 * a real regression in functionality; we take the noise. `FeatureGuard`
 * and `useFeaturePermission` already gate UI rendering on the same
 * loading window via `permissionsLoading`, so the user is never staring
 * at a misleading screen for long.
 *
 * Empty permissions object is treated as "still loading" because the BE
 * always returns at least one permission for an authenticated user; an
 * empty `{}` only appears before `fetchMyPermissions` resolves.
 */
import { store } from 'src/redux/store';

/**
 * URL-param QA override — dev/staging only (MODE !== 'production').
 * Append ?ff_<featureName>=1 to force hasFeatureAccess to return true.
 *
 * Example: ?ff_engagement-template-targeting=1
 *
 * Purpose: lets QA verify flag-gated UI without the FeatureFlag row existing
 * in the target environment's DB. Override is ephemeral (URL param only),
 * grants no server-side access — BE still gates its own endpoints.
 * Stripped by the build when MODE === 'production', so it never reaches prod.
 */
function hasFeatureAccessViaUrlParam(featureName) {
    if (import.meta.env.MODE === 'production') return false;
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get(`ff_${featureName}`) === '1';
    } catch {
        // URLSearchParams unavailable (e.g. SSR / unit tests without DOM)
        return false;
    }
}

export function hasFeatureAccess(featureName, expectedPracticeId) {
    // Dev/staging-only URL-param override — takes precedence over DB permissions.
    if (hasFeatureAccessViaUrlParam(featureName)) return true;

    const slice = store.getState()?.permissions;
    if (!slice) return true; // store/slice missing — pre-bootstrap, allow.

    // Normalise IDs once. Numeric/string coercion matters because URL
    // params arrive as strings while Redux often stores numbers.
    const expected = expectedPracticeId != null ? String(expectedPracticeId) : null;
    const snapshot = slice.practiceId != null ? String(slice.practiceId) : null;
    const target = slice.loadingPracticeId != null ? String(slice.loadingPracticeId) : null;
    const { permissions } = slice;
    const hasSnapshot = permissions && Object.keys(permissions).length > 0;

    if (slice.loading) {
        // Mid-fetch. Three cases:
        //   1. Caller named the practice → require expected===target to pass
        //      (mismatch ⇒ stale-practice fetch attempt, refuse).
        //   2. Caller did NOT name a practice, but the slice already has a
        //      snapshot for a DIFFERENT practice than the in-flight target
        //      → also refuse (legacy practice-switch protection). Without
        //      this, the dozens of `hasFeatureAccess('x')` legacy callers
        //      keep leaking 403s during the practice-switch window.
        //   3. Otherwise (true bootstrap, or in-flight for the same practice
        //      as the snapshot/empty snapshot) → fall through to the
        //      bootstrap-safe allow path below.
        if (expected != null) {
            if (target == null) return true;
            return expected === target;
        }
        if (hasSnapshot && snapshot != null && target != null && snapshot !== target) {
            return false;
        }
        // Bootstrap or in-flight same-practice refresh: allow.
        return true;
    }

    // Settled state.
    if (!hasSnapshot) return true; // pre-bootstrap, allow.

    if (expected != null && snapshot != null && expected !== snapshot) {
        // Caller named a practice and our snapshot belongs to a different
        // one — refuse the stale read.
        return false;
    }

    const level = permissions[featureName];
    return level === 'view' || level === 'manage';
}
